import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, CUMIN_API_URL } from '../../config/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const CuminIntegrationPanel = ({ embedded = false, onStatusChange } = {}) => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showCredentialsForm, setShowCredentialsForm] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [showCreateIssueForm, setShowCreateIssueForm] = useState(false);

  // Form states
  const [cuminUrl, setCuminUrl] = useState(CUMIN_API_URL);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Create issue form states
  const [issueTitle, setIssueTitle] = useState('');
  const [issueDescription, setIssueDescription] = useState('');
  const [projectId, setProjectId] = useState('');
  const [issueType, setIssueType] = useState('');
  const [issuePriority, setIssuePriority] = useState('');
  const [issueStatus, setIssueStatus] = useState('To Do');
  const [parentIssueId, setParentIssueId] = useState('');
  const [creatingIssue, setCreatingIssue] = useState(false);
  
  // CUMIN metadata
  const [projects, setProjects] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [parentIssues, setParentIssues] = useState([]);
  const [loadingParentIssues, setLoadingParentIssues] = useState(false);
  const [loadingMetadata, setLoadingMetadata] = useState(false);

  // Rich Text Editor ref
  const quillRef = useRef(null);

  // Rich Text Editor Configuration
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  const quillFormats = [
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link'
  ];

  useEffect(() => {
    loadCredentials();
  }, []);

  useEffect(() => {
    onStatusChange?.(Boolean(credentials?.isConnected));
  }, [credentials, onStatusChange]);

  useEffect(() => {
    if (credentials && credentials.isConnected) {
      loadCuminMetadata();
    }
  }, [credentials]);

  const loadCredentials = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/cumin/credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const credentialsData = response.data;
      
      // Debug: Log the response to see what we're getting
      console.log('🔍 CUMIN Credentials Response:', credentialsData);
      
      // Handle both isConnected and connected field names (Jackson serialization can vary)
      const isConnected = credentialsData.isConnected !== undefined 
        ? credentialsData.isConnected 
        : (credentialsData.connected !== undefined ? credentialsData.connected : false);
      
      console.log('🔍 Parsed isConnected value:', isConnected);
      
      // Normalize the data
      const normalizedCredentials = {
        ...credentialsData,
        isConnected: isConnected
      };
      
      console.log('🔍 Normalized credentials:', normalizedCredentials);
      
      setCredentials(normalizedCredentials);
      setCuminUrl(normalizedCredentials.cuminUrl || CUMIN_API_URL);
      setUsername(normalizedCredentials.username || '');
      setShowCredentialsForm(false);
      
      // Load metadata if connected
      if (isConnected) {
        await loadCuminMetadata();
      }
      
      return normalizedCredentials;
    } catch (error) {
      if (error.response?.status === 400 || error.response?.status === 404) {
        // No credentials found, show form
        setCredentials(null);
        setShowCredentialsForm(true);
      } else {
        setError('Failed to load CUMIN credentials');
      }
      return null;
    }
  };

  const loadCuminMetadata = async () => {
    if (!credentials || !credentials.isConnected) return;
    
    setLoadingMetadata(true);
    const token = localStorage.getItem('token');
    
    try {
      // Load projects, issue types, priorities, and statuses in parallel
      const [projectsRes, typesRes, prioritiesRes, statusesRes] = await Promise.all([
        axios.get(`${API_URL}/cumin/projects?page=0&size=100`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/cumin/issue-types`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/cumin/priorities`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/cumin/statuses`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: [] })),
      ]);

      setProjects(projectsRes.data || []);
      // Filter out Story and Epic from issue types
      const filteredIssueTypes = (typesRes.data || []).filter(t => {
        const value = t.value || t.label || t.name || t.type || '';
        return value !== 'Story' && value !== 'Epic';
      });
      setIssueTypes(filteredIssueTypes);
      setPriorities(prioritiesRes.data || []);
      setStatuses(statusesRes.data || []);
      
      // Set defaults if available - prefer Bug or Task (Story and Epic are not available)
      if (filteredIssueTypes && filteredIssueTypes.length > 0 && !issueType) {
        // Find Bug first, then Task, fallback to first item
        const preferredType = filteredIssueTypes.find(t => {
          const value = t.value || t.label || t.name || t.type || '';
          return value === 'Bug' || value === 'Task';
        });
        const defaultType = preferredType || filteredIssueTypes[0];
        setIssueType(defaultType.value || defaultType.label || defaultType.name || defaultType.type || 'Bug');
      }
      if (prioritiesRes.data && prioritiesRes.data.length > 0 && !issuePriority) {
        setIssuePriority(prioritiesRes.data[0].value || prioritiesRes.data[0].label || prioritiesRes.data[0].name || prioritiesRes.data[0].priority || 'Medium');
      }
    } catch (error) {
      console.error('Failed to load CUMIN metadata:', error);
      // Don't show error, just use empty arrays
    } finally {
      setLoadingMetadata(false);
    }
  };

  // Load parent issues when project or issue type changes
  useEffect(() => {
    const loadParentIssues = async () => {
      if (!projectId || !credentials?.isConnected) {
        setParentIssues([]);
        return;
      }

      // Only load parent issues if issueType requires a parent (Bug, Improvement, Task, Subtask)
      if (!issueType || (issueType !== 'Bug' && issueType !== 'Improvement' && issueType !== 'Task' && issueType !== 'Subtask')) {
        setParentIssues([]);
        return;
      }

      setLoadingParentIssues(true);
      const token = localStorage.getItem('token');
      
      try {
        // Always pass issueType if available, so CUMIN can filter valid parents
        const url = `${API_URL}/cumin/parent-issues?projectId=${projectId}${issueType ? `&issueType=${encodeURIComponent(issueType)}` : ''}`;
        console.log('Loading parent issues with URL:', url);
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Parent issues response:', response.data);
        setParentIssues(response.data || []);
      } catch (error) {
        console.error('Failed to load parent issues:', error);
        console.error('Error details:', error.response?.data);
        setParentIssues([]);
      } finally {
        setLoadingParentIssues(false);
      }
    };

    // Add a small delay to ensure projectId and issueType are both set
    const timer = setTimeout(() => {
      loadParentIssues();
    }, 100);

    return () => clearTimeout(timer);
  }, [projectId, issueType, credentials]);

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/cumin/credentials`,
        {
          cuminUrl,
          username,
          password,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const credentialsData = response.data;
      console.log('🔍 Save credentials response:', credentialsData);
      
      // Handle both isConnected and connected field names
      const isConnected = credentialsData.isConnected !== undefined 
        ? credentialsData.isConnected 
        : (credentialsData.connected !== undefined ? credentialsData.connected : false);
      
      const normalizedCredentials = {
        ...credentialsData,
        isConnected: isConnected
      };
      
      setCredentials(normalizedCredentials);
      setSuccess('CUMIN credentials saved and connection tested successfully!');
      setShowCredentialsForm(false);
      setPassword(''); // Clear password field
      
      // Load metadata if connected
      if (isConnected) {
        await loadCuminMetadata();
      }
    } catch (error) {
      setError(error.response?.data?.message || error.response?.data || 'Failed to save CUMIN credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/cumin/test-connection`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.connected) {
        setSuccess('Connection successful!');
        // Reload credentials to update connection status
        // Add a small delay to ensure backend has saved the status
        await new Promise(resolve => setTimeout(resolve, 500));
        const updatedCredentials = await loadCredentials();
        console.log('🔍 After test connection, updated credentials:', updatedCredentials);
        // Load metadata after credentials are loaded
        if (updatedCredentials && (updatedCredentials.isConnected || updatedCredentials.connected)) {
          await loadCuminMetadata();
        }
      } else {
        setError('Connection failed. Please check your credentials.');
        // Still reload to get updated status
        await loadCredentials();
      }
    } catch (error) {
      setError(error.response?.data?.message || error.response?.data || 'Failed to test connection');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleCreateIssue = async (e) => {
    e.preventDefault();
    setCreatingIssue(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      
      // Get HTML content from Quill editor if available
      let descriptionContent = issueDescription;
      const quillEditor = quillRef.current?.getEditor();
      if (quillEditor) {
        descriptionContent = quillEditor.root.innerHTML;
      }
      
      // Validate that description has meaningful content (not just empty HTML)
      const tmp = document.createElement('div');
      tmp.innerHTML = descriptionContent;
      const textContent = tmp.textContent || tmp.innerText || '';
      if (!textContent.trim()) {
        setError('Description is required');
        setCreatingIssue(false);
        return;
      }
      
      const response = await axios.post(
        `${API_URL}/cumin/issues`,
        {
          title: issueTitle,
          description: descriptionContent,
          projectId: parseInt(projectId),
          issueType,
          priority: issuePriority,
          status: issueStatus,
          parentIssueId: parentIssueId ? parseInt(parentIssueId) : null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess(`Issue created successfully! ${response.data.issueKey || response.data.issueId}`);
      
      // Reset form
      setIssueTitle('');
      setIssueDescription('');
      setProjectId('');
      setIssueType('');
      setIssuePriority('');
      setIssueStatus('To Do');
      setShowCreateIssueForm(false);
      
      // Clear Quill editor
      setTimeout(() => {
        const quillEditor = quillRef.current?.getEditor();
        if (quillEditor) {
          quillEditor.setText('');
        }
      }, 100);

      // Show link to issue if available
      if (response.data.url) {
        setTimeout(() => {
          window.open(response.data.url, '_blank');
        }, 1000);
      }
    } catch (error) {
      setError(error.response?.data?.message || error.response?.data || 'Failed to create issue');
    } finally {
      setCreatingIssue(false);
    }
  };

  const handleDeleteCredentials = async () => {
    if (!window.confirm('Are you sure you want to disconnect from CUMIN? This will delete your saved credentials.')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/cumin/credentials`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCredentials(null);
      setCuminUrl(CUMIN_API_URL);
      setUsername('');
      setPassword('');
      setShowCredentialsForm(true);
      setSuccess('CUMIN credentials deleted successfully');
    } catch (error) {
      setError(error.response?.data?.message || error.response?.data || 'Failed to delete credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={embedded ? '' : 'max-w-4xl mx-auto p-6'}>
      {!embedded ? (
      <div className="mb-6">
        <button
          onClick={() => navigate("/integrations")}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Plugins
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          CUMIN Integration
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Connect to CUMIN and create issues directly from WorkLoop
        </p>
      </div>
      ) : null}

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-red-600 dark:text-red-400 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-green-600 dark:text-green-400 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-green-800 dark:text-green-200">{success}</p>
          </div>
        </div>
      )}

      {/* Connection Status */}
      {credentials && (
        <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                CUMIN Connection
              </h3>
              <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  <span className="font-medium">URL:</span>{" "}
                  {credentials.cuminUrl}
                </p>
                <p>
                  <span className="font-medium">Username:</span>{" "}
                  {credentials.username}
                </p>
                <p>
                  <span className="font-medium">Status:</span>{" "}
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      credentials.isConnected
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                    }`}
                  >
                    {credentials.isConnected ? "Connected" : "Disconnected"}
                  </span>
                </p>
                {credentials.lastConnectedAt && (
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Last connected:{" "}
                    {new Date(credentials.lastConnectedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col space-y-2">
              <button
                onClick={handleTestConnection}
                disabled={testingConnection}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {testingConnection ? "Testing..." : "Test Connection"}
              </button>
              <button
                onClick={() => {
                  setShowCredentialsForm(true);
                  setShowCreateIssueForm(false);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Update Credentials
              </button>
              <button
                onClick={handleDeleteCredentials}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Disconnect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Form */}
      {showCredentialsForm && (
        <div className="mb-6 p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            {credentials ? "Update CUMIN Credentials" : "Connect to CUMIN"}
          </h2>
          <form onSubmit={handleSaveCredentials} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                CUMIN URL
              </label>
              <input
                type="text"
                value={cuminUrl}
                onChange={(e) => setCuminUrl(e.target.value)}
                placeholder={CUMIN_API_URL}
                required
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your CUMIN username"
                required
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your CUMIN password"
                required={!credentials}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
              {credentials && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Leave blank to keep current password
                </p>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Saving..." : credentials ? "Update" : "Connect"}
              </button>
              {credentials && (
                <button
                  type="button"
                  onClick={() => {
                    setShowCredentialsForm(false);
                    setPassword("");
                  }}
                  className="px-6 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Create Issue Section */}
      {credentials && credentials.isConnected && (
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Create CUMIN Issue
            </h2>
            <button
              onClick={() => {
                setShowCreateIssueForm(!showCreateIssueForm);
                if (!showCreateIssueForm) {
                  setError(null);
                  setSuccess(null);
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {showCreateIssueForm ? "Cancel" : "New Issue"}
            </button>
          </div>

          {showCreateIssueForm && (
            <form onSubmit={handleCreateIssue} className="space-y-4">
              {loadingMetadata && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Loading CUMIN metadata...
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Project *
                </label>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select a project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}{" "}
                      {project.projectKey ? `(${project.projectKey})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Issue Title *
                </label>
                <input
                  type="text"
                  value={issueTitle}
                  onChange={(e) => setIssueTitle(e.target.value)}
                  placeholder="Enter issue title"
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description *
                </label>
                <div className="relative">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={issueDescription}
                    onChange={(content) => setIssueDescription(content)}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Enter issue description"
                    className="bg-white dark:bg-gray-700 rounded-lg"
                    style={{
                      minHeight: "150px",
                      maxHeight: "300px",
                    }}
                  />
                  {/* Custom Quill Styles */}
                  <style>{`
                    .ql-container {
                      font-size: 14px;
                      min-height: 150px;
                      max-height: 300px;
                      overflow-y: auto;
                      overflow-x: hidden;
                    }
                    .ql-editor {
                      min-height: 150px;
                      color: #111827;
                      overflow-x: hidden;
                      word-wrap: break-word;
                      word-break: break-word;
                    }
                    .dark .ql-editor {
                      color: #f9fafb;
                    }
                    .ql-toolbar {
                      border-top-left-radius: 0.5rem;
                      border-top-right-radius: 0.5rem;
                      border-bottom: 1px solid #e5e7eb;
                      overflow-x: hidden !important;
                      overflow-y: hidden !important;
                      white-space: normal !important;
                      display: flex !important;
                      flex-wrap: wrap !important;
                    }
                    .ql-toolbar .ql-formats {
                      display: inline-flex !important;
                      flex-wrap: wrap !important;
                    }
                    .dark .ql-toolbar {
                      background: #374151;
                      border-bottom-color: #4b5563;
                    }
                    .dark .ql-stroke {
                      stroke: #d1d5db;
                    }
                    .dark .ql-fill {
                      fill: #d1d5db;
                    }
                    .dark .ql-picker-label {
                      color: #d1d5db;
                    }
                    .ql-container {
                      border-bottom-left-radius: 0.5rem;
                      border-bottom-right-radius: 0.5rem;
                      border: 1px solid #d1d5db;
                    }
                    .dark .ql-container {
                      border-color: #4b5563;
                    }
                  `}</style>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Issue Type
                  </label>
                  <select
                    value={issueType}
                    onChange={(e) => setIssueType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select type</option>
                    {issueTypes.map((type, idx) => (
                      <option
                        key={idx}
                        value={
                          type.value ||
                          type.label ||
                          type.name ||
                          type.type ||
                          type
                        }
                      >
                        {type.label ||
                          type.value ||
                          type.name ||
                          type.type ||
                          type}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">
                    ℹ️ Note: Epic and Story issue types are not available here.
                    Please create them directly in CUMIN.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Priority
                  </label>
                  <select
                    value={issuePriority}
                    onChange={(e) => setIssuePriority(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select priority</option>
                    {priorities.map((priority, idx) => (
                      <option
                        key={idx}
                        value={
                          priority.value ||
                          priority.label ||
                          priority.name ||
                          priority.priority ||
                          priority
                        }
                      >
                        {priority.label ||
                          priority.value ||
                          priority.name ||
                          priority.priority ||
                          priority}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <select
                    value={issueStatus}
                    onChange={(e) => setIssueStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {statuses.length > 0 ? (
                      statuses.map((status, idx) => (
                        <option
                          key={idx}
                          value={
                            status.value ||
                            status.label ||
                            status.name ||
                            status.status ||
                            status
                          }
                        >
                          {status.label ||
                            status.value ||
                            status.name ||
                            status.status ||
                            status}
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="To Do">To Do</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Done">Done</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              {/* Parent Issue Dropdown */}
              {/* Show for Bug (Story/Task/SubTask), Improvement (Story/Task/SubTask), Task (Story), Subtask (Task) */}
              {projectId &&
                (issueType === "Bug" ||
                  issueType === "Improvement" ||
                  issueType === "Task" ||
                  issueType === "Subtask") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Parent Issue{" "}
                      {(issueType === "Task" ||
                        issueType === "Subtask" ||
                        issueType === "Improvement" ||
                        issueType === "Bug") &&
                        !parentIssueId && (
                          <span className="text-red-500">*</span>
                        )}
                    </label>
                    <select
                      value={parentIssueId}
                      onChange={(e) => setParentIssueId(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      disabled={loadingParentIssues}
                    >
                      <option value="">
                        {loadingParentIssues
                          ? "Loading parent issues..."
                          : "Select parent issue (optional)"}
                      </option>
                      {parentIssues.map((issue, idx) => {
                        // Handle multiple possible field names for compatibility
                        const issueId =
                          issue.id || issue.issueId || issue.parentIssueId;
                        const issueKey =
                          issue.key ||
                          issue.issueKey ||
                          issue.parentIssueKey ||
                          issue.issue_key ||
                          "";
                        const issueTitle =
                          issue.title ||
                          issue.name ||
                          issue.parentIssueTitle ||
                          "";
                        const issueType =
                          issue.issueType ||
                          issue.issue_type ||
                          issue.type ||
                          "";
                        // Display format: HEAL-1: Web Development [Task] or Epic Name [Epic] (#123)
                        const displayText = issueKey
                          ? `${issueKey}: ${issueTitle}${issueType ? ` [${issueType}]` : ""}`
                          : `${issueTitle}${issueType ? ` [${issueType}]` : ""}${issueId ? ` (#${issueId})` : ""}`;
                        return (
                          <option key={issueId || idx} value={issueId}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                    {parentIssues.length === 0 &&
                      !loadingParentIssues &&
                      projectId && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          No parent issues available for this project. You can
                          create top-level issues.
                        </p>
                      )}
                    {(issueType === "Bug" || issueType === "Improvement") && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {issueType} requires a parent issue (Story, Task, or
                        SubTask).
                      </p>
                    )}
                  </div>
                )}

              <button
                type="submit"
                disabled={creatingIssue}
                className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingIssue ? "Creating..." : "Create Issue"}
              </button>
            </form>
          )}
        </div>
      )}

      {credentials && !credentials.isConnected && (
        <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-yellow-800 dark:text-yellow-200">
            Your CUMIN connection is not active. Please test the connection or
            update your credentials.
          </p>
        </div>
      )}
    </div>
  );
};

export default CuminIntegrationPanel;
